import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  BarChart3, PlusCircle, History, Users as UsersIcon, CreditCard,
  FileText, DollarSign, Shield, Settings, Menu, X, Calendar, Clock, Target, Webhook, MessageCircle, Bell
} from 'lucide-react';
import AlertsBadge from '../Alerts/AlertsBadge';

const Sidebar = ({ activeTab, setActiveTab }) => {
  const { isAdmin } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setIsMobileMenuOpen(false); // Close mobile menu when tab is selected
  };

  const userMenuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'add-bet', label: 'Nova Aposta', icon: PlusCircle },
    { id: 'pending-bets', label: 'Apostas Pendentes', icon: Clock },
    { id: 'bet-history', label: 'Histórico', icon: History },
    { id: 'goals', label: 'Metas de Lucro', icon: Target },
    { id: 'groups', label: 'Grupos', icon: UsersIcon },
    { id: 'bankroll', label: 'Gestão de Banca', icon: CreditCard },
    { id: 'expenses', label: 'Outras Despesas', icon: DollarSign },
    { id: 'user-reports', label: 'Relatórios', icon: Calendar },
    { id: 'alerts-settings', label: 'Alertas', icon: Bell },
    { id: 'subscription', label: 'Minha Assinatura', icon: Settings },
  ];

  const adminMenuItems = [
    { id: 'admin-dashboard', label: 'Dashboard Admin', icon: Shield },
    { id: 'manage-users', label: 'Gerenciar Usuários', icon: UsersIcon },
    { id: 'sales', label: 'Vendas', icon: DollarSign },
    { id: 'webhook', label: 'Webhook', icon: Webhook },
    { id: 'reports', label: 'Relatórios Marketing', icon: FileText },
    { id: 'settings', label: 'Configurações', icon: Settings },
  ];

  const menuItems = isAdmin ? adminMenuItems : userMenuItems;

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={toggleMobileMenu}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700"
      >
        {isMobileMenuOpen ? (
          <X className="w-6 h-6 text-gray-600 dark:text-gray-400" />
        ) : (
          <Menu className="w-6 h-6 text-gray-600 dark:text-gray-400" />
        )}
      </button>

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-40 
          w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700
          transform transition-transform duration-300 ease-in-out
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Logo/Header - Hidden on mobile when menu is closed */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700 lg:block">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {isAdmin ? 'Painel Admin' : 'Menu Principal'}
              </h2>
              {!isAdmin && <AlertsBadge />}
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;

              return (
                <button
                  key={item.id}
                  onClick={() => handleTabClick(item.id)}
                  className={`
                    w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left
                    transition-colors duration-200
                    ${isActive
                      ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }
                  `}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  <span className="font-medium">{item.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            {/* Botão de Suporte */}
            <a
              href="https://wa.me/5511916560595"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full px-4 py-3 mb-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium text-sm"
            >
              <MessageCircle className="w-4 h-4" />
              <span>Suporte WhatsApp</span>
            </a>
            
            <div className="text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Stake Mind v2.7
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500">
                © 2025
              </p>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 z-30 bg-black bg-opacity-50"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
    </>
  );
};

export default Sidebar;